import {
  CSSProperties,
  DragEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  ControlButton,
  Edge,
  getOutgoers,
  IsValidConnection,
  MarkerType,
  MiniMap,
  Node,
  NodeChange,
  NodeDragHandler,
  OnConnect,
  OnConnectEnd,
  OnConnectStart,
  OnEdgesDelete,
  OnNodesDelete,
  BackgroundVariant,
  ReactFlowInstance,
  SelectionDragHandler,
  useEdgesState,
  useNodesState,
  useOnViewportChange,
  useReactFlow,
  Viewport,
  XYPosition,
} from 'reactflow';

import { Box, Button, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { Maximize, MessagesSquare, Network, Search, ZoomIn, ZoomOut } from 'lucide-react';
import { EditorLayout } from './navbar/EditorLayout';
import { EditorStatus } from './navbar/EditorStatus';
import { TranslationsToolbarPill } from './components/translations/TranslationsToolbarPill';
import { CanvasLanguageBar } from './components/translations/CanvasView/CanvasLanguageBar';
import { FindBar } from './components/ReactFlow/FindBar/FindBar';
import { useConversationFind } from './components/ReactFlow/FindBar/useConversationFind';
import { FindHighlightProvider } from './components/ReactFlow/FindBar/FindHighlightContext';
import { OverrideWorkbenchModal } from './components/translations/OverrideEditor/OverrideWorkbenchModal';
import { CanvasTranslationProvider } from './components/translations/CanvasView/CanvasTranslationContext';
import { TranslatedBlockNode } from './components/translations/CanvasView/TranslatedBlockNode';
import { useCanvasTranslation } from './components/translations/CanvasView/useCanvasTranslation';
import {
  languageDir,
  ENGLISH_LANGUAGE_CODE,
} from '@duke-hq/shared-constants/courseTranslationLanguages';
import { EditorBreadcrumbs } from './navbar/EditorBreadcrumbs';
import { EditableResourceTitle } from './navbar/EditableResourceTitle';
import { FlowPublishButton } from './navbar/FlowPublishButton';
import { ConversationVersionHistory } from './navbar/FlowVersionHistory';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds as flowEditorTestIds } from '@duke-hq/app-admin-web/FlowEditor/testIds';
import 'reactflow/dist/style.css';
import { useDebouncedCallback } from 'use-debounce';

import { EndNode } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/EndNode';
import {
  getIsStartNode,
  nodeColor,
  centreOnNode,
  computeEndNodeIds,
  hydrateOutgoers,
  lastPersonaOfNode,
  seedNodeDimensions,
  parseFlowContent,
  removeOutgoersForDeletedNodes,
  setOutgoersForNode,
  recomputeOutgoersForNode,
  canDropNewNodeFromHandle,
  FLOW_EDGE_TYPE,
} from '@duke-hq/app-admin-web/components/ReactFlow/utils';
import { motion } from 'framer-motion';
import ButtonEdge from '@duke-hq/app-admin-web/components/ReactFlow/edges/ButtonEdge';
import { OffscreenEdges } from '@duke-hq/app-admin-web/components/ReactFlow/edges/OffscreenEdges';
import { getLayoutedElements } from './components/ReactFlow/autoLayout';
import { animateNodePositions } from './components/ReactFlow/animateNodePositions';
import { FlowEditorStateContext } from '@duke-hq/app-admin-web/components/ReactFlow/FlowEditorStateContext';
import {
  MemoizedBlockNode,
  checkOptionsConnected,
} from '@duke-hq/app-admin-web/components/ReactFlow/nodes/FlowEditorBlockNode';
import type { NodeData } from '@duke-hq/app-admin-web/components/ReactFlow/nodes/flowEditorBlockNodeTypes';
import { CanvasSimulationPanel } from '@duke-hq/app-admin-web/components/ReactFlow/sidebars/CanvasSimulationPanel';
import { ref } from 'valtio';
import { createId } from '@paralleldrive/cuid2';
import { subscribeKey } from 'valtio/utils';
import { useAutosaveFeedback } from '@duke-hq/app-admin-web/components/ReactFlow/useAutosaveFeedback';
import { useStaleEditorCheck } from '@duke-hq/app-admin-web/components/ReactFlow/useStaleEditorCheck';
import { useLoadLatest } from '@duke-hq/app-admin-web/components/ReactFlow/useLoadLatest';
import { RouterOutputs, trpcAdmin } from './lib/trpcAdmin';
import { MemoizedDefaultContainer } from '@duke-hq/app-admin-web/components/ReactFlow/containers/DefaultContainer';
import { flowEditorState } from '@duke-hq/app-admin-web/components/ReactFlow/flowEditorState';
import { isFlowContentEqual } from '@duke-hq/app-admin-web/components/ReactFlow/contentEquality';
import { useUndoRecorder } from '@duke-hq/app-admin-web/components/ReactFlow/useUndoRecorder';
import { undoInspectorState } from './components/ReactFlow/undoInspector/undoInspectorState';
import { UndoInspectorPanel } from './components/ReactFlow/undoInspector/UndoInspectorPanel';
import { loadViewport, saveViewport } from './components/ReactFlow/flowEditorViewportStorage';
import { PersonaResolverProvider } from '@duke-hq/app-admin-web/components/ReactFlow/PersonaResolverContext';
import { chatPersonaMenuState } from '@duke-hq/app-admin-web/components/ReactFlow/tiptap/flowDBlock/chatPersonaMenuState';

const proOptions = {
  hideAttribution: true,
};

const onDragOver = (event: DragEvent) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const defaultEdgeOptions = {
  style: {
    strokeWidth: 2,
  },
  markerEnd: {
    type: MarkerType.ArrowClosed,
  },
  type: FLOW_EDGE_TYPE,
};

const edgeTypes = {
  [FLOW_EDGE_TYPE]: ButtonEdge,
};

export const nodeTypes = {
  start: MemoizedBlockNode,
  end: EndNode,
  block: MemoizedBlockNode,
  defaultContainer: MemoizedDefaultContainer,
};

// Idle debounce before an autosave fires. Shortened from 4s: 4s let enough
// unsaved work pile up that authors started to wonder, and best-in-class editors
// sit around 2-3s (GitLab documents 3s after the last keypress). The visible
// "Saving..."/"Unsaved" indicator is debounced separately (see useAutosaveFeedback)
// so a snappier save cadence does not make the indicator flash.
const SAVE_CHANGES_TIMEOUT = 2500;

// Viewport persistence is local-only, so it can settle much faster than the
// document autosave above. The debounce is load-bearing: each save rewrites the
// stored array, so keeping it off the per-frame path (where viewport changes
// fire) is what keeps the cost negligible. Don't shorten it without revisiting
// the storage shape.
const VIEWPORT_SAVE_TIMEOUT = 1000;

// The simulation panel floats over the right edge (absolute, so it never reflows
// the graph) and slides in via a transform. Fixed width because it's absolute with
// percentage-width content, so shrink-to-fit can't size it to the toolbar; 448 fits
// the toolbar (with the longest endonym) and a comfortable reading column.
const SIMULATION_PANEL_WIDTH = pxToRem(448);
const SIMULATION_PANEL_STYLE: CSSProperties = {
  zIndex: 1000,
  position: 'absolute',
  right: '0',
  height: '100%',
  top: '0',
};

// How long the nodes take to glide into place when the author hits Tidy up, and
// how long the camera then takes to ease out to fit the result. Kept short so
// the editor feels snappy rather than cinematic.
const TIDY_UP_ANIMATION_DURATION = 400;
const VIEWPORT_FIT_DURATION = 400;

// ProseMirror's `toJSON()` omits `content` when the doc has no children, so
// editor write paths can land `{type:'doc'}` (no `content` key) in node state,
// which then persists as a `ZodError`-triggering shape. Default to an empty
// array here so a single boundary covers every writer.
export function normaliseTiptapContent<F extends { nodes: Node[] }>(flow: F): F {
  return {
    ...flow,
    nodes: flow.nodes.map((node) => {
      const data = node.data;
      if (data === null || typeof data !== 'object' || !('tiptap' in data)) {
        return node;
      }
      const tiptap = data.tiptap;
      if (tiptap === null || typeof tiptap !== 'object') {
        return node;
      }
      if ('content' in tiptap && Array.isArray(tiptap.content)) {
        return node;
      }
      // eslint-disable-next-line no-console
      console.warn('FlowEditor: normalising tiptap with missing content array', {
        nodeId: node.id,
      });
      return {
        ...node,
        data: { ...data, tiptap: { ...tiptap, content: [] } },
      };
    }),
  };
}

export function FlowEditor({ data }: { data: RouterOutputs['resources']['byId'] }) {
  const {
    simulationDisclosure,
    simulationIsOpen,
    hiddenSimulation,
    setHiddenSimulation,
    commitSnapshot,
    activeNode,
    setActiveNode,
    canUndo,
    canRedo,
    undo,
    redo,
    simulationButtonProps,
    lastPublishedResourceVersion,
  } = useContext(FlowEditorStateContext);
  const utils = trpcAdmin.useUtils();

  const { setCenter, getNode, getNodes, getEdges, fitView, zoomIn, zoomOut, screenToFlowPosition } =
    useReactFlow();

  const flowDivRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const updateConversation = trpcAdmin.resourceVersions.update.useMutation({
    onSuccess(input) {
      // Refresh only the edited resource. The autosave runs on every batch of
      // edits, so refetching the whole resource list here (which nothing on the
      // editor screen renders) was wasted work on each save.
      utils.resources.byId.invalidate(input.resource_id);
    },
  });

  const renameResource = trpcAdmin.resources.update.useMutation({
    onSuccess() {
      utils.resources.byId.invalidate(data.id);
      utils.resources.all.refetch();
    },
  });

  // The draft revision this tab is editing from. Sent on every save so the server
  // can reject a stale write (another tab or editor saved since we loaded), and
  // updated from each successful save so subsequent saves stay in sync.
  const revisionRef = useRef(data.draftVersion?.revision ?? 0);

  // The viewport this resource was last left at, read once at mount. When set
  // it is restored via the defaultViewport prop; when absent (first time opening
  // this conversation) initialiseViewport centres on the start node instead.
  const savedViewport = useRef<Viewport | null>(loadViewport(data.id));

  const persistViewport = useDebouncedCallback((viewport: Viewport) => {
    saveViewport(data.id, viewport);
  }, VIEWPORT_SAVE_TIMEOUT);

  // useOnViewportChange fires for every viewport change (pane drag, scroll
  // zoom, minimap, and the zoom buttons), unlike the onMoveEnd prop which only
  // covers pane gestures.
  useOnViewportChange({
    onEnd: (viewport) => persistViewport(viewport),
  });

  // On first open (no saved viewport) start at the conversation's start node
  // rather than fitting the whole graph, which on large flows zooms everything
  // out too far to read. The defaultViewport prop handles the restore case.
  const initialiseViewport = (instance: ReactFlowInstance) => {
    if (savedViewport.current) {
      return;
    }
    const startNode = nodes.find((node) => node.type === 'start');
    if (startNode) {
      centreOnNode(instance.setCenter, startNode);
    } else {
      instance.fitView({ maxZoom: 1 });
    }
  };

  // Node positions recorded at the moment a drag begins. React Flow's drag is
  // backed by d3-drag, which fires its start event on mousedown before any
  // movement, so a plain click looks identical to a drag at the start. We hold
  // the pre-drag positions here and only mark the flow dirty at drag stop if a
  // node actually moved, so a click does not light up the undo button.
  const dragStartPositions = useRef(new Map<string, XYPosition>());
  // The handle a connection drag started from, captured in onConnectStart so
  // onConnectEnd can create-and-connect a node when the drag is dropped on the
  // empty pane rather than onto another node's handle.
  const connectingHandle = useRef<{ nodeId: string; handleId: string | null } | null>(null);
  // Set when onConnect handles a drop onto a real target handle. onConnectEnd
  // still fires for the same gesture, and its pane check can pass even over a
  // node (event.target resolves to the pane beneath), so it reads this to skip
  // its create-a-node branch and avoid spawning a spurious extra node.
  const connectionMade = useRef(false);

  const autosaveFeedback = useAutosaveFeedback('conversation');

  const { isStale } = useStaleEditorCheck({
    resourceVersionId: data.draftVersion?.id,
    getLocalRevision: () => revisionRef.current,
  });
  const { requestLoadLatest, confirmDialog: loadLatestConfirmDialog } =
    useLoadLatest('conversation');

  const { nodes: initialNodes, edges: initialEdges } = parseFlowContent(data.draftVersion?.content);

  const [nodes, setNodes, onNodesChange] = useNodesState<NodeData>(
    seedNodeDimensions(hydrateOutgoers(initialNodes, initialEdges)),
  );

  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // The content as last persisted (the loaded draft, then each successful save).
  // The autosave gate compares the about-to-save document against this and skips
  // the write when they match, so a no-op edit or a typed-then-reverted edit
  // sends nothing and settles the flow back to clean. Seeded in `onInit` from
  // the same `toObject()` path the save uses, so the comparison is shape-for-
  // shape with the save payload.
  const lastSavedContentRef = useRef<{ nodes: Node[]; edges: Edge[] } | null>(null);

  useEffect(() => {
    flowEditorState.setConnectedOptionIds(checkOptionsConnected(nodes, edges));
    flowEditorState.setEndNodeIds(computeEndNodeIds(nodes, edges));
  }, [nodes, edges]);

  // -- Canvas translation lens ------------------------------------------------
  const [lensTarget, setLensTarget] = useState<string | null>(null);
  const [lensShowingEnglish, setLensShowingEnglish] = useState(false);

  const lensOpen = lensTarget != null;
  const lensLanguage = lensShowingEnglish ? null : lensTarget;

  const openLens = useCallback((language: string) => {
    setLensTarget(language);
    setLensShowingEnglish(false);
  }, []);
  const closeLens = useCallback(() => {
    setLensTarget(null);
    setLensShowingEnglish(false);
  }, []);

  const [editLanguage, setEditLanguage] = useState<string | null>(null);
  const goToWorkbench = useCallback((language: string) => {
    setEditLanguage(language);
  }, []);

  // -- Find in conversation ---------------------------------------------------
  // Search runs against the English source only (there is no per-language
  // index), so opening it closes the translation lens.
  const find = useConversationFind(nodes);
  const { open: openFindPanel } = find;
  const openFind = useCallback(() => {
    closeLens();
    openFindPanel();
  }, [closeLens, openFindPanel]);

  // The publish context persists across resources; without the resource_id guard a
  // publish in one conversation would leak its version into every other's queries.
  const publishedResourceVersionId =
    lastPublishedResourceVersion?.resource_id === data.id
      ? lastPublishedResourceVersion.id
      : data.last_published_version_id;

  const canvasTranslation = useCanvasTranslation({
    resourceId: data.id,
    resourceVersionId: publishedResourceVersionId ?? 0,
    language: lensLanguage,
    enabled: lensLanguage != null && publishedResourceVersionId != null,
  });

  const lensDir = lensLanguage == null ? 'ltr' : languageDir(lensLanguage);

  // Memoised: a fresh nodeTypes object every render would remount every node.
  const activeNodeTypes = useMemo(
    () =>
      !lensOpen
        ? nodeTypes
        : { ...nodeTypes, start: TranslatedBlockNode, block: TranslatedBlockNode },
    [lensOpen],
  );

  // Nodes read the in-text match cue from context, so `nodes` passes through
  // to ReactFlow untouched.
  const findHighlight = useMemo(
    () => ({
      query: find.isOpen ? find.query.trim().toLowerCase() : '',
      currentNodeId: find.currentNodeId,
      matchedNodeIds: find.matchedNodeIds,
      currentRun: find.currentRun,
    }),
    [find.isOpen, find.query, find.currentNodeId, find.matchedNodeIds, find.currentRun],
  );

  const canvasTranslationValue = useMemo(
    () =>
      lensTarget == null
        ? null
        : {
            language: lensLanguage ?? ENGLISH_LANGUAGE_CODE,
            dir: lensDir,
            rowsByNode: lensShowingEnglish ? new Map() : canvasTranslation.rowsByNode,
            // Edit targets the lens language, never the English source.
            onEditNode: () => goToWorkbench(lensTarget),
          },
    [
      lensTarget,
      lensLanguage,
      lensShowingEnglish,
      lensDir,
      canvasTranslation.rowsByNode,
      goToWorkbench,
    ],
  );

  const { data: contentPersonas } = trpcAdmin.contentPersonas.all.useQuery();

  // Seed the editor-wide default persona ONCE, from the loaded flow rather than
  // the first library persona. New blocks fall back to this default, so seeding
  // it from `contentPersonas[0]` made every fresh flow (and every new block)
  // start as whoever happened to be first in the library. Prefer the persona of
  // the last node that already has one (the conversation's most recent speaker);
  // only use the library's first entry when the flow is genuinely empty. Guarded
  // so it runs once on load and never clobbers a default updated by later edits.
  const seededDefaultPersona = useRef(false);
  useEffect(() => {
    if (seededDefaultPersona.current || !contentPersonas?.length) {
      return;
    }
    const lastSpeaker = nodes
      .map((node) => lastPersonaOfNode(node))
      .filter((id): id is string => !!id)
      .at(-1);
    flowEditorState.defaultPersonaId = lastSpeaker ?? contentPersonas[0]?.id ?? null;
    seededDefaultPersona.current = true;
  }, [contentPersonas, nodes]);

  useEffect(() => {
    if (flowEditorState.simulationActive && activeNode) {
      // TODO: add blue round shadow highlight on active node handle (connecting dot)

      // if the active node is a containerized node
      if (activeNode.parentNode) {
        const activeContainer = getNode(activeNode.parentNode) as Node;
        setCenter(
          activeContainer?.position.x + (activeNode.position.x || 0),
          activeContainer?.position.y + activeNode.position.y + (activeNode.height || 0) / 2,
          {
            zoom: 1,
            duration: 800,
          },
        );
      }
      // if the active node is not a containerized node
      else {
        setCenter(
          activeNode?.position.x + (activeNode.width || 0),
          activeNode?.position.y + (activeNode.height || 0),
          {
            zoom: 1,
            duration: 800,
          },
        );
      }
    }
  }, [activeNode]);

  function handleNodeChange(changes: NodeChange[]) {
    const nextChanges = changes.reduce((acc, change) => {
      if (change.type === 'remove') {
        const node = getNode(change.id);
        if (node && getIsStartNode(node)) {
          return acc;
        }
      }

      return [...acc, change];
    }, [] as NodeChange[]);

    onNodesChange(nextChanges);
  }

  const onConnect: OnConnect = useCallback(
    (connection) => {
      connectionMade.current = true;
      flowEditorState.markDirty();

      let newEdges: Edge[] = edges;
      setEdges((edges) => {
        newEdges = addEdge(connection, edges);
        return newEdges;
      });

      const sourceNode = nodes.find((node) => node.id === connection.source);
      if (sourceNode) {
        const outgoers = getOutgoers(sourceNode, nodes, newEdges).map((n) => n.id);
        setNodes((nds) => setOutgoersForNode(nds, sourceNode.id, outgoers));
      }
    },
    [setEdges],
  );

  const onConnectStart = useCallback<OnConnectStart>((_event, { nodeId, handleId }) => {
    connectingHandle.current = nodeId ? { nodeId, handleId } : null;
    connectionMade.current = false;
  }, []);

  // Add Node On Edge Drop: if a connection drag is released over the empty pane
  // (not onto a target handle), create a new block at the drop point and connect
  // the dragged-from handle to it, so authors can extend a flow in one gesture.
  const onConnectEnd = useCallback<OnConnectEnd>(
    (event) => {
      const source = connectingHandle.current;
      const madeConnection = connectionMade.current;
      connectingHandle.current = null;
      connectionMade.current = false;
      if (!source) {
        return;
      }

      // onConnect already wired this drop to a real target handle, so don't also
      // create a node: the pane check below can pass even when dropping over a
      // node, which would otherwise spawn a spurious extra one.
      if (madeConnection) {
        return;
      }

      const targetIsPane =
        event.target instanceof Element && event.target.classList.contains('react-flow__pane');
      if (!targetIsPane) {
        return;
      }

      const sourceNode = getNode(source.nodeId);
      if (!canDropNewNodeFromHandle(sourceNode, getEdges(), source.handleId)) {
        return;
      }

      const point = 'changedTouches' in event ? event.changedTouches[0] : event;
      if (!point) {
        return;
      }
      const { clientX, clientY } = point;

      // Mark dirty so the drop-to-create autosaves; the history recorder captures
      // the node-add, edge-add and outgoers update as one coalesced undo entry.
      flowEditorState.markDirty();

      // The new block continues the conversation it was dragged from, so its
      // first message should speak as the source block's last speaker rather than
      // the library default. The node starts empty, so seed the editor-wide
      // default persona (which the first typed message inherits) from the source.
      const sourcePersona = lastPersonaOfNode(sourceNode);
      if (sourcePersona) {
        flowEditorState.defaultPersonaId = sourcePersona;
      }

      const newNodeId = createId();
      const newNode: Node<NodeData> = {
        id: newNodeId,
        type: 'block',
        position: screenToFlowPosition({ x: clientX, y: clientY }),
        data: { label: `block ${getNodes().length + 1}` },
        dragHandle: '.drag-handle',
      };
      const newEdge: Edge = {
        id: `${source.nodeId}-${newNodeId}`,
        source: source.nodeId,
        sourceHandle: source.handleId,
        target: newNodeId,
        // Match defaultEdgeOptions so the dropped edge renders as the custom
        // bezier edge with an arrow immediately, not a plain edge until reload.
        type: FLOW_EDGE_TYPE,
        markerEnd: { type: MarkerType.ArrowClosed },
      };

      let nextEdges: Edge[] = [];
      setEdges((eds) => {
        nextEdges = eds.concat(newEdge);
        return nextEdges;
      });

      // Add the new node selected (and deselect the rest) so its editor mounts
      // immediately and the author can type straight away. Only the selected node
      // renders EditableNodeContent; without this the drop-to-create node showed
      // the read-only StaticBlockContent and swallowed every keystroke. Recompute
      // the source's outgoers off the list that already holds the new node, so its
      // add-node menu isn't wrongly shown.
      setNodes((nds) =>
        recomputeOutgoersForNode(
          nds.map((n) => ({ ...n, selected: false })).concat({ ...newNode, selected: true }),
          source.nodeId,
          nextEdges,
        ),
      );
      setActiveNode(newNode);
    },
    [screenToFlowPosition, getNode, getNodes, getEdges, setNodes, setEdges, setActiveNode],
  );

  const isValidConnection: IsValidConnection = useCallback((connection) => {
    const nodes = getNodes();
    const edges = getEdges();
    const sourceNode = nodes.find((node) => node.id === connection.source);
    const targetNode = nodes.find((node) => node.id === connection.target);

    const hasCycle = (node: Node, visited = new Set()) => {
      if (visited.has(node.id)) {
        return false;
      }

      visited.add(node.id);

      for (const outgoer of getOutgoers(node, nodes, edges)) {
        if (outgoer.id === connection.source) {
          return true;
        }
        if (hasCycle(outgoer, visited)) {
          return true;
        }
      }
    };

    if (targetNode?.id === connection.source) {
      return false;
    }
    if (targetNode && hasCycle(targetNode)) {
      return false;
    }

    const numberOfSourceOutgoers = sourceNode && getOutgoers(sourceNode, nodes, getEdges())?.length;

    const hasOptions = !!sourceNode?.data?.options?.length;
    const hasMultipleOutgoers = numberOfSourceOutgoers !== 0;

    return hasOptions || !hasMultipleOutgoers;
  }, []);

  const onEdgesDelete: OnEdgesDelete = useCallback((deletedEdges: Edge[]) => {
    flowEditorState.markDirty();

    deletedEdges.forEach((deletedEdge) => {
      const sourceNode = nodes.find((node) => node.id === deletedEdge.source);
      if (!sourceNode) {
        return;
      }
      const remaining = sourceNode.data.outgoers?.filter((id) => id !== deletedEdge.target) ?? [];
      setNodes((nds) => setOutgoersForNode(nds, sourceNode.id, remaining));
    });
  }, []);

  /**
   * Function for saving react flow to DB
   */
  const onSave = useCallback(
    async (versionToSave: number) => {
      // Once a save has been rejected as stale, every later autosave conflicts
      // again until the page is reloaded (we never advance revisionRef past a
      // conflict). Stop re-firing them so one stale tab raises a single
      // conflict rather than a fresh one on every debounce.
      if (autosaveFeedback.hasConflict()) {
        return;
      }

      // Read the ReactFlow instance straight from the proxy at call time, not via
      // a render-tracked snapshot. The instance is set once in onInit and never
      // changes, and a save only fires well after init, so a debounced save always
      // sees it. Tracking it through useSnapshot would re-render the whole canvas on
      // every clientVersion bump (i.e. every keystroke), which is the thing the
      // editor must never do.
      if (flowEditorState.flowEditor) {
        const flow = flowEditorState.flowEditor.toObject();
        const normalisedFlow = normaliseTiptapContent(flow);

        // The authoritative genuine-edit gate. clientVersion is a cheap "something
        // poked me" nudge (a blur, a selection-only transaction, a reverted
        // keystroke can all bump it), so before sending we check the content
        // actually differs from what we last persisted. A no-op marks the flow
        // saved (so isDirty settles and the indicator clears) and sends nothing.
        // If the baseline isn't seeded yet (a save somehow fired before onInit)
        // we fall through and save, never the other way, so no edit is dropped.
        if (
          lastSavedContentRef.current &&
          isFlowContentEqual(normalisedFlow, lastSavedContentRef.current)
        ) {
          flowEditorState.markSaved(versionToSave);
          autosaveFeedback.onIdle();
          return;
        }

        if (data?.draftVersion?.id) {
          autosaveFeedback.onSaving();
          // Measure the whole-document save cost (payload size + round-trip), so
          // the dev inspector can show whether whole-doc saving is getting
          // expensive enough to warrant moving to deltas.
          undoInspectorState.lastSavePayloadBytes = JSON.stringify(normalisedFlow).length;
          const saveStartedAt = performance.now();
          try {
            const saved = await updateConversation.mutateAsync({
              content: normalisedFlow,
              resourceVersionId: data.draftVersion?.id,
              baseRevision: revisionRef.current,
            });
            undoInspectorState.lastSaveDurationMs = Math.round(performance.now() - saveStartedAt);
            revisionRef.current = saved.revision;
            lastSavedContentRef.current = normalisedFlow;
            autosaveFeedback.onSaved();
            flowEditorState.markSaved(versionToSave);
          } catch (error) {
            // On conflict we intentionally do not advance revisionRef: refreshing
            // is the recovery path. If the user keeps editing without reloading,
            // each autosave conflicts again, which is preferable to silently
            // overwriting the newer content the other tab saved.
            autosaveFeedback.onSaveError(error);
          }
        } else {
          autosaveFeedback.onSaveError(new Error('Missing draft version'));
        }
      }
    },
    [autosaveFeedback, updateConversation, data?.draftVersion?.id],
  );

  // Record where the dragged node(s) started so drag stop can tell a real move
  // from a plain click: d3-drag fires onDragStart on mousedown, so a click
  // reaches drag stop with its start position unchanged and must leave the flow
  // clean. The history recorder captures the moved graph off the dirty signal.
  const onDragStart = useCallback((draggedNodes: Node[]) => {
    dragStartPositions.current = new Map(
      draggedNodes.map((node) => [node.id, { ...node.position }]),
    );
  }, []);

  // Mark dirty only if a dragged node actually moved, so a click leaves the undo
  // stack and the save indicator untouched.
  const onDragStop = useCallback((draggedNodes: Node[]) => {
    const moved = draggedNodes.some((node) => {
      const start = dragStartPositions.current.get(node.id);
      return !start || start.x !== node.position.x || start.y !== node.position.y;
    });
    dragStartPositions.current.clear();
    if (moved) {
      flowEditorState.markDirty();
    }
  }, []);

  const onNodeDragStart: NodeDragHandler = useCallback(
    (_event, _node, nodes) => {
      onDragStart(nodes);
    },
    [onDragStart],
  );

  const onNodeDragStop: NodeDragHandler = useCallback(
    (_event, _node, nodes) => {
      onDragStop(nodes);
    },
    [onDragStop],
  );

  const onSelectionDragStart: SelectionDragHandler = useCallback(
    (_event, nodes) => {
      onDragStart(nodes);
    },
    [onDragStart],
  );

  const onSelectionDragStop: SelectionDragHandler = useCallback(
    (_event, nodes) => {
      onDragStop(nodes);
    },
    [onDragStop],
  );

  const onNodesDelete: OnNodesDelete = useCallback(
    (deletedNodes) => {
      flowEditorState.markDirty();
      setActiveNode(null);
      const deletedIds = new Set(deletedNodes.map((n) => n.id));
      setNodes((nds) => removeOutgoersForDeletedNodes(nds, deletedIds));
    },
    [setActiveNode, setNodes],
  );

  const onPaneClick = () => {
    setActiveNode(null);
  };

  const cancelTidyUpAnimation = useRef<(() => void) | null>(null);

  const onTidyUp = useCallback(() => {
    const current = getNodes();
    const { nodes: layouted } = getLayoutedElements(current, getEdges(), { direction: 'LR' });

    const targets = new Map<string, XYPosition>(layouted.map((node) => [node.id, node.position]));
    const starts = new Map<string, XYPosition>(current.map((node) => [node.id, node.position]));

    // The whole re-layout is one undoable step. Land the final positions, THEN
    // markDirty, so the dirty bump and the committed move are in the same turn:
    // the recorder reads node positions as content, so a bump raised before the
    // positions move would diff an unchanged graph, gate it as a no-op, and miss
    // the move that lands afterwards. Mirrors onDragStop (setNodes then markDirty).
    // The per-frame animation moves never markDirty, so only this final landing
    // records, as one entry.
    const landFinalLayout = () => {
      setNodes((nodes) =>
        nodes.map((node) => ({ ...node, position: targets.get(node.id) ?? node.position })),
      );
      flowEditorState.markDirty();
      fitView({ maxZoom: 1, duration: VIEWPORT_FIT_DURATION });
    };

    cancelTidyUpAnimation.current?.();

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      landFinalLayout();
      return;
    }

    cancelTidyUpAnimation.current = animateNodePositions({
      from: starts,
      to: targets,
      durationMs: TIDY_UP_ANIMATION_DURATION,
      onFrame: setNodes,
      onDone: landFinalLayout,
    });
  }, [getNodes, getEdges, setNodes, fitView]);

  useEffect(() => () => cancelTidyUpAnimation.current?.(), []);

  // Autosave is driven by a valtio subscription rather than by reading
  // clientVersion during render, so a keystroke (which only bumps
  // flowEditorState.clientVersion) never re-renders this component or the
  // canvas. The debounced callback fires SAVE_CHANGES_TIMEOUT after the last
  // edit and saves whatever clientVersion is current at that moment.
  const debouncedSave = useDebouncedCallback(() => {
    // The debugging switch suppresses only the automatic save; Cmd+S (forceSave)
    // still persists on demand.
    if (undoInspectorState.autosaveDisabled) {
      return;
    }
    // Don't autosave from a backgrounded tab. A tab left open in the background
    // is the usual source of stale-revision CONFLICTs (it saves over content a
    // foreground tab wrote); pausing while hidden removes most of them at the
    // source. The unmount flush and a refocus edit still persist real work, and
    // Cmd+S always saves regardless.
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    const version = flowEditorState.clientVersion;
    if (version > 0) {
      onSave(version);
    }
  }, SAVE_CHANGES_TIMEOUT);

  // Manual save (Cmd/Ctrl+S): flush the active editor's buffered keystrokes into
  // node.data, cancel the pending autosave, and persist immediately. Works even
  // when autosave is disabled, so a tester can save exactly when they choose.
  const forceSave = useCallback(() => {
    flowEditorState.flushActiveEditor();
    debouncedSave.cancel();
    const version = flowEditorState.clientVersion;
    if (version > 0) {
      onSave(version);
    }
  }, [debouncedSave, onSave]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 's' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        forceSave();
        return;
      }
      // Hijack browser find: on a panned, partly virtualised canvas it misses
      // most of the conversation.
      if (event.key === 'f' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        openFind();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [forceSave, openFind]);

  // -- Undo history recorder ---------------------------------------------------
  // The undo recorder: turns committed graph changes into undo entries (one per
  // genuine edit, coalescing a continuous typing run). Lives in its own hook so
  // its timing-sensitive logic is testable in isolation; it registers its
  // interface (getCurrentGraph/applyRestoredGraph/noteTextEdit) on the singleton.
  const { recordHistory, seedBaseline } = useUndoRecorder({ nodes, edges, commitSnapshot });

  // One subscription drives the indicator, the autosave and the undo recorder off
  // the single dirty signal, so every edit path (structural, text, options,
  // persona) shows "Unsaved changes" the instant it marks the flow dirty, records
  // an undo entry, then the debounced save runs. A no-op that the save gate
  // rejects calls markSaved + onIdle, which clears isDirty and settles the
  // indicator back to "Saved".
  useEffect(() => {
    return subscribeKey(flowEditorState, 'clientVersion', () => {
      if (flowEditorState.isDirty) {
        autosaveFeedback.onUnsaved();
      }
      debouncedSave();
    });
  }, [debouncedSave, autosaveFeedback]);

  // Record undo history off the COMMITTED graph, not the clientVersion bump. The
  // bump fires synchronously inside an edit, but a structural edit replaces the
  // nodes/edges arrays via setNodes, which React only commits on the next render;
  // a microtask recorder (off the valtio bump) ran BEFORE that commit and read
  // the pre-edit arrays, so add/rename/delete looked like no-ops. This effect
  // runs AFTER each commit, when `nodes`/`edges` (and `liveGraphRef`) are the new
  // graph, so the diff against the last recorded baseline is real. `nodes`/`edges`
  // are listed so the effect fires on every committed graph change.
  useEffect(() => {
    recordHistory();
  }, [nodes, edges, recordHistory]);

  // Fill the layout container (LayoutAdmin renders us inside a flex box that is
  // `calc(100vh - navbar)` tall) rather than breaking out to the full viewport.
  return (
    <PersonaResolverProvider>
      <EditorLayout
        heightMode="fill"
        toolbar={{
          breadcrumbs: (
            <EditorBreadcrumbs
              crumbs={[
                { label: t`Units`, to: '/units' },
                ...(data.unitOfCompetency
                  ? [
                      {
                        label: data.unitOfCompetency.code || data.unitOfCompetency.title,
                        to: `/units/${data.unitOfCompetency.id}`,
                      },
                    ]
                  : []),
              ]}
              current={
                <EditableResourceTitle
                  title={data.title ?? ''}
                  onRename={async (value) => {
                    const previousTitle = flowEditorState.resource?.title;
                    if (flowEditorState.resource) {
                      flowEditorState.resource.title = value;
                    }
                    // Drive the shared save indicator so renaming the title gives
                    // the same Saving/Saved feedback as editing the conversation.
                    autosaveFeedback.onSaving();
                    try {
                      await renameResource.mutateAsync({ id: data.id, title: value });
                      autosaveFeedback.onSaved();
                    } catch (error) {
                      // Roll back the optimistic title here, then rethrow so the
                      // header reverts its own displayed value too.
                      if (flowEditorState.resource && previousTitle !== undefined) {
                        flowEditorState.resource.title = previousTitle;
                      }
                      autosaveFeedback.onSaveError(error);
                      // The reverted title leaves nothing to save, so don't strand
                      // the indicator on "Unsaved Changes". Settle it back to idle
                      // unless the conversation itself still has pending edits.
                      if (flowEditorState.clientVersion === flowEditorState.serverVersion) {
                        autosaveFeedback.onIdle();
                      }
                      throw error;
                    }
                  }}
                  ariaLabel={t`Conversation title`}
                />
              }
            />
          ),
          undoRedo: { canUndo, canRedo, onUndo: undo, onRedo: redo },
          staleEditor: { isStale, entityLabel: 'conversation', onLoadLatest: requestLoadLatest },
          status: (
            <EditorStatus
              published={{
                isPublished: data.last_published_version_id != null,
                publishedVersion: data.lastPublishedVersion?.version,
                publishedAt: data.lastPublishedVersion?.published_at,
                hasUnpublishedChanges: data.hasUnpublishedChanges,
              }}
              versionHistory={
                <ConversationVersionHistory
                  resourceId={data.id}
                  draftVersionId={data.draftVersion?.id}
                  draftRevision={data.draftVersion?.revision ?? 0}
                  livePublishedVersion={data.lastPublishedVersion?.version}
                  getBaseRevision={() => revisionRef.current}
                />
              }
              derivedOutputs={
                <TranslationsToolbarPill
                  resourceId={data.id}
                  resourceVersionId={publishedResourceVersionId}
                  resourceKind="CONVERSATION"
                  publishedVersion={data.lastPublishedVersion?.version}
                  onViewLanguage={openLens}
                  onEditLanguage={goToWorkbench}
                />
              }
            />
          ),
          actions: [
            {
              id: 'preview',
              node: (
                <>
                  {/* Full button when there's room; icon-only when narrow so the
                    action row stays compact without hiding Preview. */}
                  <Button
                    {...simulationButtonProps()}
                    variant="secondary"
                    size="sm"
                    display={{ base: 'none', md: 'inline-flex' }}
                  >
                    Preview
                  </Button>
                  <Tooltip label="Preview">
                    <IconButton
                      {...simulationButtonProps()}
                      variant="ghost"
                      size="sm"
                      aria-label="Preview"
                      display={{ base: 'inline-flex', md: 'none' }}
                    >
                      <MessagesSquare size={iconSize.md} />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            },
            {
              id: 'publish',
              node: (
                <FlowPublishButton isStale={isStale} getBaseRevision={() => revisionRef.current} />
              ),
            },
          ],
        }}
        body={{
          kind: 'canvas',
          children: (
            // The canvas + side panel. The panel is absolutely positioned, so this
            // row Flex is its positioning context and they size to its height.
            <Flex flexDir="row" flex="1" minH={0} w="100%" position="relative" overflow="hidden">
              {lensTarget != null ? (
                <CanvasLanguageBar
                  language={lensTarget}
                  showingEnglish={lensShowingEnglish}
                  isLoading={canvasTranslation.isLoading}
                  isError={canvasTranslation.isError}
                  onShowEnglish={() => setLensShowingEnglish(true)}
                  onShowTarget={() => setLensShowingEnglish(false)}
                  onEditTranslation={() => goToWorkbench(lensTarget)}
                  onClose={closeLens}
                />
              ) : null}
              {find.isOpen ? (
                <FindBar
                  query={find.query}
                  matchCount={find.matches.length}
                  currentIndex={find.currentIndex}
                  onQueryChange={find.setQuery}
                  onNext={find.next}
                  onPrevious={find.previous}
                  onClose={find.close}
                />
              ) : null}
              <CanvasTranslationProvider value={canvasTranslationValue}>
                <FindHighlightProvider value={findHighlight}>
                  <Box
                    flexGrow="1"
                    h="100%"
                    ref={wrapperRef}
                    data-testid={flowEditorTestIds.canvas}
                    sx={{
                      // ReactFlow's control buttons are a touch small. Nudge the buttons
                      // and their icons up so they're comfortably clickable.
                      '.react-flow__controls-button': {
                        width: pxToRem(28),
                        height: pxToRem(28),
                      },
                      '.react-flow__controls-button svg': {
                        maxWidth: pxToRem(16),
                        maxHeight: pxToRem(16),
                      },
                    }}
                  >
                    <ReactFlow
                      //TODO: Solve all types
                      ref={flowDivRef}
                      nodes={nodes}
                      edges={edges}
                      // ReactFlow ignores these keys while a text field is focused (isInputDOMNode);
                      // the start node is protected in handleNodeChange. Null in the lens: a selection
                      // made before it opened would otherwise still be deletable.
                      deleteKeyCode={lensOpen ? null : ['Backspace', 'Delete']}
                      nodeTypes={activeNodeTypes}
                      nodesDraggable={!lensOpen}
                      nodesConnectable={!lensOpen}
                      elementsSelectable={!lensOpen}
                      // Only mount nodes intersecting the viewport. Large flows mount
                      // every node (and its Tiptap editor) up front, which is the bulk of
                      // the initial-load cost on the biggest conversations.
                      onlyRenderVisibleElements
                      onNodesDelete={onNodesDelete}
                      onNodesChange={handleNodeChange}
                      onNodeDragStart={onNodeDragStart}
                      onNodeDragStop={onNodeDragStop}
                      onClick={() => {
                        chatPersonaMenuState.close();
                      }}
                      onEdgesChange={onEdgesChange}
                      onEdgesDelete={onEdgesDelete}
                      onConnect={onConnect}
                      onConnectStart={onConnectStart}
                      onConnectEnd={onConnectEnd}
                      isValidConnection={isValidConnection}
                      edgeTypes={edgeTypes}
                      onSelectionDragStart={onSelectionDragStart}
                      onSelectionDragStop={onSelectionDragStop}
                      // onDrop={onDrop}
                      onDragOver={onDragOver}
                      onPaneClick={onPaneClick}
                      proOptions={proOptions}
                      selectNodesOnDrag={false}
                      defaultEdgeOptions={defaultEdgeOptions}
                      // Restore the last viewport for this resource if we have one.
                      // Otherwise onInit centres on the start node (see initialiseViewport).
                      {...(savedViewport.current ? { defaultViewport: savedViewport.current } : {})}
                      zoomOnScroll={false}
                      zoomOnDoubleClick={false}
                      onInit={(instance: ReactFlowInstance) => {
                        flowEditorState.flowEditor = ref(instance);
                        initialiseViewport(instance);
                        // Seed the saved-content baseline from the same serialise path
                        // the save uses, so the first autosave gate compares like with
                        // like (see lastSavedContentRef).
                        lastSavedContentRef.current = normaliseTiptapContent(instance.toObject());
                        // Seed the undo recorder's baseline so the first edit records the
                        // loaded graph as its "before".
                        seedBaseline(instance.toObject());
                      }}
                      snapToGrid={true}
                    >
                      {/* Custom control buttons (the built-in zoom/fit/lock are off) so
                  every button has a consistent title tooltip and aria-label. */}
                      <Controls showZoom={false} showFitView={false} showInteractive={false}>
                        <ControlButton
                          onClick={openFind}
                          title="Find in conversation"
                          aria-label="Find in conversation"
                        >
                          <Search />
                        </ControlButton>
                        <ControlButton
                          onClick={() => zoomIn()}
                          title="Zoom in"
                          aria-label="Zoom in"
                        >
                          <ZoomIn />
                        </ControlButton>
                        <ControlButton
                          onClick={() => zoomOut()}
                          title="Zoom out"
                          aria-label="Zoom out"
                        >
                          <ZoomOut />
                        </ControlButton>
                        <ControlButton
                          onClick={() => fitView({ maxZoom: 1, duration: VIEWPORT_FIT_DURATION })}
                          title="Fit view"
                          aria-label="Fit view"
                        >
                          <Maximize />
                        </ControlButton>
                        <ControlButton onClick={onTidyUp} title="Tidy up" aria-label="Tidy up">
                          <Network />
                        </ControlButton>
                      </Controls>
                      <MiniMap nodeColor={nodeColor} zoomable pannable />

                      <OffscreenEdges edges={edges} />

                      <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
                    </ReactFlow>
                    {/* Dev instrument: shows the live undo/redo stacks + recorder log.
                      Off by default; renders a small enable button until switched on.
                      Outside <ReactFlow> so the zoom transform doesn't scale it. */}
                    <UndoInspectorPanel />
                  </Box>
                </FindHighlightProvider>
              </CanvasTranslationProvider>
              {editLanguage != null && publishedResourceVersionId != null ? (
                <OverrideWorkbenchModal
                  isOpen
                  onClose={() => setEditLanguage(null)}
                  resourceId={data.id}
                  // Translations key on the published version; same id the lens reads.
                  resourceVersionId={publishedResourceVersionId}
                  resourceKind="CONVERSATION"
                  publishedVersion={data.lastPublishedVersion?.version ?? null}
                  language={editLanguage}
                />
              ) : null}
              {/* Only the id is taken from the disclosure: it pairs with the trigger's
                  aria-controls. Spreading the rest hands motion.div DOM handlers whose
                  signatures clash with framer-motion's, and hidden is set below. */}
              <motion.div
                id={simulationDisclosure().id}
                hidden={hiddenSimulation}
                initial={false}
                onAnimationStart={() => setHiddenSimulation(false)}
                onAnimationComplete={() => setHiddenSimulation(!simulationIsOpen)}
                // Slide in/out with a transform (closed = shifted fully off the
                // right edge), on a plain ease-out tween so it settles cleanly
                // without the default spring's overshoot.
                animate={{ x: simulationIsOpen ? '0%' : '100%' }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                style={{ ...SIMULATION_PANEL_STYLE, width: SIMULATION_PANEL_WIDTH }}
              >
                <CanvasSimulationPanel lastPublishedVersionId={data.last_published_version_id} />
              </motion.div>
            </Flex>
          ),
        }}
      >
        {loadLatestConfirmDialog}
      </EditorLayout>
    </PersonaResolverProvider>
  );
}
